import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useFormContext } from '../../context/FormContext';
import { Header } from './Header';
import { VideoPlaceholder } from '../shared/VideoPlaceholder';
import { MethodSelector } from '../shared/MethodSelector';
import { ContactDetails } from '../shared/ContactDetails';
import { WorkbookUpload } from '../upload/WorkbookUpload';
import { CompleteForm } from '../form/CompleteForm';
import { SummaryPanel } from '../shared/SummaryPanel';
import { ShortcutMenu } from '../ui/ShortcutMenu';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

const { FiCheckCircle, FiDownload } = FiIcons;

export const PageLayout = () => {
  const { data, resetForm } = useFormContext();

  if (data.isSubmitted) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col">
        <Header />
        <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-12 flex items-center justify-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">
            <Card className="text-center py-16 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-brand-accent"></div>
              <div className="w-24 h-24 bg-brand-success/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <SafeIcon icon={FiCheckCircle} className="text-5xl text-brand-success" />
              </div>
              <h1 className="text-4xl font-serif text-brand-text mb-4">Submission successful</h1>
              <p className="text-lg text-brand-muted mb-10 max-w-lg mx-auto leading-relaxed">
                Thank you for completing the Writs & Giggles RFI. Your data has been securely received and indexed for our legal assessment team.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button onClick={resetForm} variant="primary" className="h-12 px-8">Submit another RFI</Button>
                <Button variant="outline" className="h-12 px-8">
                  <SafeIcon icon={FiDownload} className="mr-2" /> Download Receipt
                </Button>
              </div>
            </Card>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative bg-brand-bg scroll-smooth">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-12 py-8">
        <div className="mb-12 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <h4 className="text-xs font-bold text-brand-muted tracking-widest uppercase mb-4">Internal Intake Portal</h4>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-brand-text mb-6">Submit the Writs & Giggles RFI.</h1>
            <p className="text-lg text-brand-muted leading-relaxed max-w-2xl">
              Choose a submission method, add your details, and either upload the completed workbook or fill in our structured digital form.
            </p>
          </motion.div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 pb-32">
          <div className="flex-1 max-w-3xl relative">
            <VideoPlaceholder />
            <MethodSelector />
            
            <AnimatePresence mode="wait">
              <motion.div
                key={data.method}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {/* Horizontal Progress Bar only shows in Form Method */}
                {data.method === 'form' && <ShortcutMenu />}
                
                <div id="contact"><ContactDetails /></div>
                {data.method === 'upload' ? <WorkbookUpload /> : <CompleteForm />}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="w-full lg:w-[380px] shrink-0">
            <SummaryPanel />
          </div>
        </div>
      </main>
    </div>
  );
};